'use client'

import {usePathname, useSearchParams} from "next/navigation"
import React, {Suspense, useEffect, useSyncExternalStore} from "react"

import posthog from 'posthog-js'
import {PostHogProvider as PHProvider, usePostHog} from 'posthog-js/react'
import {useLogger} from "@/lib/use-logger";

export function PostHog({children}: { children: React.ReactNode }) {
    const log = useLogger(import.meta.url);
    useEffect(() => {
        // browser‐only
        const hostname = window.location.hostname;
        const isLocalhost =
          hostname === 'localhost' ||
          hostname === '127.0.0.1';

        // flag is always 'true' or 'false' because it's defined in all environments
        const flagOn = process.env.NEXT_PUBLIC_POSTHOG_ENABLE === 'true';

        // capture in prod (non‐localhost) OR when explicitly forced on via flag
        const shouldCapture = flagOn || !isLocalhost;

        if (shouldCapture) {
            posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
                api_host: "/imlvc4l94i",
                ui_host: 'https://eu.posthog.com',
                person_profiles: 'always',
                capture_pageview: false, // Disable automatic pageview capture, as we capture manually
                capture_pageleave: true,
            })

        } else {
            log.debug("posthog.opt_out_capturing on localhost");

            posthog.init('fake token', {
                api_host: "/imlvc4l94i",
                ui_host: 'https://eu.posthog.com',
                autocapture: false,
                disable_session_recording: true,
                loaded: function (ph) {
                    ph.opt_out_capturing(); // opts a user out of event capture
                },
            })

        }
    }, [log])

    return (
        <PHProvider client={posthog}>
            <SuspendedPostHogPageView/>
            {children}
        </PHProvider>
    )
}

function PostHogPageView() {
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const posthog = usePostHog()

    // Track pageviews
    useEffect(() => {
        if (pathname && posthog) {
            let url = window.origin + pathname
            if (searchParams.toString()) {
                url = url + "?" + searchParams.toString();
            }

            posthog.capture('$pageview', {'$current_url': url})
        }
    }, [pathname, searchParams, posthog])

    return null
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
const emptySubscribe = () => () => {};
function SuspendedPostHogPageView() {
    const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false);
    if (!mounted) return null;
    return (
        <Suspense fallback={null}>
            <PostHogPageView />
        </Suspense>
    );
}